import "./UsuariosPage.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { UsuarioPlataformaResponse } from "../../../shared/response/seguridad/UsuarioPlataformaResponse";
import { UsuarioPlataformaService } from "../../services/seguridad/UsuarioPlataformaService";
import { UsuarioFormModal } from "./UsuarioFormModal";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";

// ── Constants ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#0095E2", "#2EB054", "#8B5CF6", "#EF9F27", "#E24B4A",
  "#0EA5E9", "#F59E0B", "#10B981", "#6366F1", "#EC4899",
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function initials(nombre: string): string {
  const parts = (nombre || "").trim().split(/\s+/);
  const n = parts[0]?.[0] ?? "";
  const a = parts[1]?.[0] ?? "";
  return (n + a).toUpperCase();
}

function avatarColor(nombre: string): string {
  const s = nombre || "";
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface RoleInfo { label: string; css: string }

function primaryRole(u: UsuarioPlataformaResponse): RoleInfo {
  if (u.hasSuperAdmin)                      return { label: "Admin",     css: "usr-role-admin" };
  if (u.hasComercial)                       return { label: "Comercial", css: "usr-role-com" };
  if (u.hasPilotos)                         return { label: "Piloto",    css: "usr-role-pil" };
  if (u.hasHardware)                        return { label: "Soporte",   css: "usr-role-sup" };
  if (u.hasConductores)                     return { label: "Conductor", css: "usr-role-con" };
  if (u.hasOficina)                         return { label: "Oficina",   css: "usr-role-ofi" };
  return { label: "Sin rol", css: "usr-role-none" };
}

function sinPermisos(u: UsuarioPlataformaResponse): boolean {
  return !u.hasSuperAdmin && !u.hasComercial &&
         !u.hasHardware && !u.hasPilotos && !u.hasConductores && !u.hasOficina;
}

// ── UsuariosPage ───────────────────────────────────────────────────────────────

/**
 * Menú Usuarios. Los superusuarios no llegan del servidor (2026-10-14). En Nitrofert (`_soloRoles`) los usuarios
 * nacen al entrar por el portal de Nitria: no hay alta ni baja, y del formulario solo se edita el rol (el servidor
 * hace cumplir lo mismo).
 */
export class UsuariosPage extends BasePageComponent {
  /** `soloRoles`: marca Nitrofert, donde desde acá solo se asignan roles. */
  constructor(private readonly _soloRoles = false) { super(); }

  private _all:      UsuarioPlataformaResponse[] = [];
  private _filtered: UsuarioPlataformaResponse[] = [];

  private _tbody!:       HTMLTableSectionElement;
  private _totEl!:       HTMLSpanElement;
  private _searchInput!: HTMLInputElement;
  private _roleFilter!:  HTMLSelectElement;
  private _statFilter!:  HTMLSelectElement;
  private _kActivos!:    HTMLElement;
  private _kInactivos!:  HTMLElement;
  private _kRoles!:      HTMLElement;
  private _kSinPermisos!: HTMLElement;

  public getAppRoute(): string { return "/admin/usuarios"; }
  public getPageTitle(): string | null { return "Usuarios"; }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page usr-page";
    pageContent.innerHTML = `
      <div class="usr-content">

        <div class="usr-pg-hd">
          <div>
            <div class="usr-pg-ey">Administrativo</div>
            <div class="usr-pg-ti">Usuarios</div>
            <div class="usr-pg-su">${this._soloRoles
              ? "Los usuarios se crean al entrar por el portal de Nitria. Aquí solo se asignan roles."
              : "Accesos y permisos de la plataforma"}</div>
          </div>
          ${this._soloRoles ? "" : `
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="usr-nuevo-btn">
            <i class="ph ph-plus" aria-hidden="true"></i> Nuevo usuario
          </button>`}
        </div>

        <div class="usr-kstrip">
          <div class="usr-kcard">
            <div class="usr-k-val green" id="usr-k-activos">—</div>
            <div class="usr-k-lbl">USUARIOS ACTIVOS</div>
          </div>
          <div class="usr-kcard">
            <div class="usr-k-val red" id="usr-k-inactivos">—</div>
            <div class="usr-k-lbl">INACTIVOS</div>
          </div>
          <div class="usr-kcard">
            <div class="usr-k-val blue" id="usr-k-roles">—</div>
            <div class="usr-k-lbl">ROLES DISTINTOS</div>
          </div>
          <div class="usr-kcard">
            <div class="usr-k-val gray" id="usr-k-sin-permisos">—</div>
            <div class="usr-k-lbl">SIN PERMISOS</div>
          </div>
        </div>

        <div class="usr-frow">
          <div class="usr-sw">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input class="usr-si" id="usr-search" placeholder="Buscar nombre o correo…">
          </div>
          <select class="usr-fsel" id="usr-filter-rol">
            <option value="">Todos los roles</option>
            <option value="Admin">Admin</option>
            <option value="Comercial">Comercial</option>
            <option value="Piloto">Piloto</option>
            <option value="Soporte">Soporte</option>
            <option value="Conductor">Conductor</option>
            <option value="Oficina">Oficina</option>
            <option value="Sin rol">Sin rol</option>
          </select>
          <select class="usr-fsel" id="usr-filter-stat">
            <option value="">Todos</option>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
          <span class="usr-ftot" id="usr-tot"></span>
        </div>

        <div class="usr-tbl-wrap">
          <table class="usr-tbl">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody id="usr-tbody">
              <tr><td colspan="4" class="usr-empty">Cargando…</td></tr>
            </tbody>
          </table>
        </div>

      </div>
    `;

    this._tbody       = document.getElementById("usr-tbody")! as HTMLTableSectionElement;
    this._totEl       = document.getElementById("usr-tot")!;
    this._searchInput = document.getElementById("usr-search")! as HTMLInputElement;
    this._roleFilter  = document.getElementById("usr-filter-rol")! as HTMLSelectElement;
    this._statFilter  = document.getElementById("usr-filter-stat")! as HTMLSelectElement;
    this._kActivos    = document.getElementById("usr-k-activos")!;
    this._kInactivos  = document.getElementById("usr-k-inactivos")!;
    this._kRoles      = document.getElementById("usr-k-roles")!;
    this._kSinPermisos = document.getElementById("usr-k-sin-permisos")!;

    const nuevoBtn = document.getElementById("usr-nuevo-btn");
    if (nuevoBtn) nuevoBtn.onclick = () => this._onCreateClick();
    this._searchInput.oninput  = () => this._filterAndRender();
    this._roleFilter.onchange  = () => this._filterAndRender();
    this._statFilter.onchange  = () => this._filterAndRender();

    this._loadData();
  }

  protected override beforeDispose(): void {}

  // ── Data ──────────────────────────────────────────────────────────────────────

  private async _loadData(): Promise<void> {
    try {
      // Los superusuarios no se listan ni cuentan en KPIs: el servidor ya no los manda (2026-10-14).
      this._all = await UsuarioPlataformaService.instance.get();
      this._filterAndRender();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar usuarios", (error as Error).message);
    }
  }

  // ── Filtering & rendering ─────────────────────────────────────────────────────

  private _filterAndRender(): void {
    const q    = this._searchInput.value.toLowerCase();
    const fRol = this._roleFilter.value;
    const fSt  = this._statFilter.value;

    this._filtered = this._all.filter((u) => {
      const matchQ = !q || u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      const matchR = !fRol || primaryRole(u).label === fRol;
      const matchS = !fSt  || (fSt === "activo" ? u.isActivo !== false : u.isActivo === false);
      return matchQ && matchR && matchS;
    });

    this._renderKpi();
    this._renderTable();
  }

  private _renderKpi(): void {
    const activos    = this._all.filter((u) => u.isActivo !== false).length;
    const inactivos  = this._all.filter((u) => u.isActivo === false).length;
    const roles      = new Set(this._all.map((u) => primaryRole(u).label)).size;
    const sinPerm    = this._all.filter(sinPermisos).length;

    this._kActivos.textContent    = String(activos);
    this._kInactivos.textContent  = String(inactivos);
    this._kRoles.textContent      = String(roles);
    this._kSinPermisos.textContent = String(sinPerm);
  }

  private _renderTable(): void {
    const list = this._filtered;
    this._totEl.textContent = `${list.length} usuario${list.length === 1 ? "" : "s"}`;

    if (list.length === 0) {
      this._tbody.innerHTML = `<tr><td colspan="4" class="usr-empty">Sin resultados</td></tr>`;
      return;
    }

    this._tbody.innerHTML = list
      .map((u, i) => {
        const role    = primaryRole(u);
        const color   = avatarColor(u.nombre);
        const inits   = initials(u.nombre);
        const isActivo = u.isActivo !== false;
        const estado  = isActivo
          ? `<span class="usr-stat-ok"><span class="usr-stat-dot"></span>Activo</span>`
          : `<span class="usr-stat-na"><span class="usr-stat-dot"></span>Inactivo</span>`;
        return `
          <tr data-idx="${i}">
            <td>
              <div class="usr-user-cell">
                <div class="usr-avatar" style="background:${color}">${this._esc(inits)}</div>
                <div>
                  <div class="usr-cell-name">${this._esc(u.nombre)}</div>
                  <div class="usr-cell-email">${this._esc(u.email)}</div>
                </div>
              </div>
            </td>
            <td><span class="usr-role-badge ${role.css}">${this._esc(role.label)}</span></td>
            <td>${estado}</td>
            <td>
              <button class="sx-btn sx-btn-secondary sx-btn-sm" data-idx="${i}">Editar</button>
            </td>
          </tr>`;
      })
      .join("");

    this._tbody.querySelectorAll<HTMLButtonElement>("button[data-idx]").forEach((btn) => {
      btn.onclick = () => this._onEditClick(list[Number(btn.dataset.idx)]);
    });

    }

  // ── Actions ───────────────────────────────────────────────────────────────────

  private _onCreateClick(): void {
    new UsuarioFormModal(
      (saved) => {
        this._all = [saved, ...this._all];
        this._filterAndRender();
      },
    ).init();
  }

  /** Abre el formulario de edición. En Nitrofert sin baja: solo se edita el rol. */
  private _onEditClick(item: UsuarioPlataformaResponse): void {
    new UsuarioFormModal(
      (saved) => {
        this._all = this._all.map((u) => (u.id === saved.id ? saved : u));
        this._filterAndRender();
      },
      this._soloRoles
        ? undefined
        : (deletedId) => {
            this._all = this._all.filter((u) => u.id !== deletedId);
            this._filterAndRender();
          },
      item,
      this._soloRoles,
    ).init();
  }

  // ── Utils ─────────────────────────────────────────────────────────────────────

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
